import classNames from 'classnames';
import React, { useCallback, useRef } from 'react';
import Select, { GroupBase, SelectInstance, components } from 'react-select';
import { withInjectedProps } from '../InjectedPropsContext';
import './Dropdown.scss';
import { defaultFilterOption } from './filters';
import {
  DropdownWrapper,
  ISharedDropdownProps,
  toListItemValue,
  useChangeHandler,
  useKeepMenuUnderDropdownEffect,
} from './shared';

export type IDropdownProps = ISharedDropdownProps & {
  focusedOption?: any;
  isClearable?: boolean;
  isSearchable?: boolean;
  isMulti?: boolean;
};

const DropdownBase: React.FC<IDropdownProps> = (props) => {
  const {
    defaultValue,
    disabled,
    focusedOption,
    id,
    focusRef,
    isClearable = true, // clearable by default
    isLoading,
    isMulti = false,
    isSearchable = true, // searchable by default
    maxResults = Number.MAX_SAFE_INTEGER,
    name,
    onInputChange,
    onKeyDown,
    onMenuOpen,
    options = [],
    placeholder = 'Select...',
    severity,
    filterOption = defaultFilterOption,
  } = props;

  const cx = classNames('Dropdown', { [`Dropdown-${severity}`]: !!severity });

  /*
   * Transform the value to an appropriate type.
   * (We either want an option or a simple value.)
   */
  const handleChange = useChangeHandler(props);
  const dropdownValue = toListItemValue(props);

  /*
   * Move to the selected option (ie the one in the value prop) when the menu is opened.
   */
  const selectRef = useRef<SelectInstance<any, boolean, GroupBase<any>> | null>(null);

  const setFocusedOption = useCallback(() => {
    // Calling an initial prop if it has been passed
    onMenuOpen?.(undefined);
    if (!isMulti) {
      // Getting a selected option
      if (focusedOption) {
        setTimeout(() => {
          // Setting a focused value as a selected one
          // References:
          // - https://github.com/JedWatson/react-select/blob/master/packages/react-select/src/Select.js#L503
          // - https://github.com/JedWatson/react-select/blob/master/packages/react-select/src/Select.js#L802
          if (selectRef.current) {
            let option = focusedOption;
            let focusableOptions = selectRef.current.getFocusableOptions();
            let selectedIndex = focusableOptions.indexOf(option);
            //If the option can't be found, attempt to find it by value
            if (selectedIndex < 0) {
              option = focusableOptions.find((o) => o.value === option.value);
              selectedIndex = focusableOptions.indexOf(option);
            }
            if (selectedIndex >= 0) {
              // Focusing selected option only if it exists
              selectRef.current.scrollToFocusedOptionOnUpdate = true;
              selectRef.current.setState({
                focusedValue: null,
                focusedOption: option,
              });
            }
          }
        });
      }
    }
  }, [focusedOption, onMenuOpen, isMulti]);

  /*
   * Keep the dropdown menu positioned underneath the dropdown when the viewport is scrolled.
   */
  const menuPortalRef = useRef<HTMLDivElement | null>(null);
  const MenuPortal = (props: any) => <components.MenuPortal {...props} innerRef={menuPortalRef} />;

  useKeepMenuUnderDropdownEffect(selectRef, menuPortalRef);

  return (
    <DropdownWrapper disabled={disabled}>
      <Select
        className={cx}
        classNamePrefix="Dropdown"
        components={{ MenuPortal }}
        defaultValue={defaultValue as any}
        filterOption={filterOption}
        inputId={id}
        inputValue={undefined}
        isClearable={isClearable}
        isDisabled={disabled}
        isLoading={isLoading}
        isMulti={isMulti}
        isSearchable={isSearchable}
        loadingMessage={() => 'Searching...'}
        menuPlacement="auto"
        menuPortalTarget={document.body}
        name={name}
        noOptionsMessage={({ inputValue }) => (inputValue ? 'No matches.' : 'Start typing to search.')}
        onChange={handleChange}
        onInputChange={onInputChange}
        onKeyDown={onKeyDown as any}
        onMenuOpen={focusedOption ? (setFocusedOption as any) : onMenuOpen}
        options={options.slice(0, maxResults)}
        placeholder={isLoading ? 'Loading...' : placeholder}
        ref={(reactSelect) => {
          selectRef.current = reactSelect;

          // Create an object that looks like a regular input (HTMLInputElement) so the ref looks like any other ref.
          if (focusRef) {
            (focusRef as any).current = reactSelect
              ? ({
                  disabled: reactSelect.props.isDisabled,
                  focus: reactSelect.focus.bind(reactSelect),
                } as HTMLSelectElement)
              : null;
          }
        }}
        /*
         * Styles can't be directly applied to the portal element because it doesn't use the classNamePrefix prop.
         * https://github.com/JedWatson/react-select/issues/2909
         */
        styles={{ menuPortal: (styles) => ({ ...styles, zIndex: 9999 }) }}
        value={dropdownValue}
      />
    </DropdownWrapper>
  );
};

const Dropdown = withInjectedProps(DropdownBase);

export { Dropdown, DropdownBase };
